/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

/**
 * SSE (Server-Sent Events) 工具类
 * 用于处理服务端推送数据
 */

export interface SSEOptions {
  url: string;
  onMessage?: (data: any) => void;
  onError?: (error: Event) => void;
  onOpen?: () => void;
  headers?: Record<string, string>;
  withCredentials?: boolean;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}

export class SSEClient {
  private abortController: AbortController | null = null;

  private options: SSEOptions;

  private reconnectAttempts = 0;

  private reconnectTimer: NodeJS.Timeout | null = null;

  private isClosed = false;

  private readyState: number = 2; // 0: CONNECTING, 1: OPEN, 2: CLOSED

  constructor(options: SSEOptions) {
    this.options = {
      reconnectInterval: 3000,
      maxReconnectAttempts: 5,
      ...options,
    };
  }

  /**
   * 连接 SSE (使用 fetch API 支持自定义 headers)
   */
  async connect() {
    if (this.abortController) {
      this.close();
    }

    this.isClosed = false;
    this.readyState = 0; // CONNECTING

    try {
      // 获取 API Key
      const apiKey = this.getApiKey();
      
      // 构建请求头
      const headers: Record<string, string> = {
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache',
        ...this.options.headers,
      };

      // 如果自定义 headers 中没有 Authorization，则使用 API Key
      if (!headers.Authorization && apiKey) {
        headers.Authorization = apiKey;
        headers['x-api-key'] = apiKey;
      }

      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #00d4ff');
      console.log('%c🔌 [SSE] 开始连接', 'color: #00d4ff; font-weight: bold; font-size: 13px');
      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #00d4ff');
      console.log('%c📋 [URL]:', 'color: #1890ff; font-weight: bold');
      console.log(this.options.url);
      console.log('%c📋 [Headers]:', 'color: #faad14; font-weight: bold');
      console.log(JSON.stringify(headers, null, 2));
      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #00d4ff');

      this.abortController = new AbortController();

      const response = await fetch(this.options.url, {
        method: 'GET',
        headers,
        signal: this.abortController.signal,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      this.readyState = 1; // OPEN

      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #52c41a');
      console.log('%c✅ [SSE] 连接成功', 'color: #52c41a; font-weight: bold; font-size: 13px');
      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #52c41a');
      console.log('%c📊 [连接状态]:', 'color: #52c41a; font-weight: bold');
      console.log('ReadyState:', this.readyState);
      console.log('HTTP Status:', response.status);
      console.log('重连次数已重置为: 0');
      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #52c41a');
      console.log(''); // 空行
      
      this.reconnectAttempts = 0;
      this.options.onOpen?.();

      // 读取流数据
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      if (!reader) {
        throw new Error('无法获取响应流');
      }

      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #13c2c2');
      console.log('%c🎧 [SSE] 开始监听服务端数据...', 'color: #13c2c2; font-weight: bold; font-size: 13px');
      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #13c2c2');
      console.log('等待服务端推送消息...');
      console.log('提示: 如果长时间没有数据，可能是服务端暂时没有预警数据');
      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #13c2c2');
      console.log(''); // 空行

      // 处理 SSE 消息的函数
      const processMessage = (data: string) => {
        // 忽略空数据
        if (!data || !data.trim()) {
          return;
        }
        
        console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #722ed1');
        console.log('%c📨 [SSE] 收到消息', 'color: #722ed1; font-weight: bold; font-size: 13px');
        console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #722ed1');
        console.log('%c📋 [Raw Data]:', 'color: #722ed1; font-weight: bold');
        console.log(data);
        
        try {
          const parsedData = JSON.parse(data);
          console.log('%c📋 [Parsed Data]:', 'color: #722ed1; font-weight: bold');
          console.log(JSON.stringify(parsedData, null, 2));
          console.log('%c✅ 触发 onMessage 回调', 'color: #52c41a; font-weight: bold');
          console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #722ed1');
          console.log(''); // 空行
          
          this.options.onMessage?.(parsedData);
        } catch (error) {
          console.log('%c⚠️ [警告]: 无法解析为 JSON，尝试使用原始数据', 'color: #faad14; font-weight: bold');
          console.log('解析错误:', error);
          console.log('%c✅ 触发 onMessage 回调（原始数据）', 'color: #52c41a; font-weight: bold');
          console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #722ed1');
          console.log(''); // 空行
          
          this.options.onMessage?.({ message: data, raw: true });
        }
      };

      // eslint-disable-next-line no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        
        if (done) {
          console.log('%c🔌 [SSE] 服务器关闭连接', 'color: #d9d9d9');
          // 处理缓冲区中剩余的数据
          if (buffer.trim()) {
            processMessage(buffer.trim());
          }
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        
        // 打印接收到的原始数据块（用于调试）
        console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #13c2c2');
        console.log('%c📦 [SSE] 收到数据块!', 'color: #13c2c2; font-weight: bold; font-size: 14px');
        console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #13c2c2');
        console.log('时间:', new Date().toLocaleTimeString());
        console.log('数据块长度:', chunk.length);
        console.log('数据块内容:');
        console.log(`%c${chunk}`, 'color: #00d4ff; background: #1a1a2e; padding: 4px 8px; border-radius: 4px;');
        console.log('当前缓冲区总长度:', buffer.length);
        console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #13c2c2');
        
        // 处理 SSE 消息
        // SSE 标准格式: 每条消息以空行（\n\n 或 \r\n\r\n）分隔
        // 但有些服务器可能发送非标准格式，我们需要兼容处理
        
        // 方式1: 按双换行符分割消息（标准 SSE 格式）
        const messages = buffer.split(/\n\n|\r\n\r\n/);
        buffer = messages.pop() || ''; // 保留未完成的消息
        
        console.log(`检测到 ${messages.length} 条完整消息`);

        for (const message of messages) {
          if (!message.trim()) continue;
          
          console.log('处理消息块:', `${message.substring(0, 100)}${message.length > 100 ? '...' : ''}`);
          
          // 解析消息中的各个字段
          const lines = message.split(/\r?\n/);
          let eventType = 'message';
          let eventData = '';
          
          for (const line of lines) {
            if (line.startsWith('event:')) {
              eventType = line.slice(6).trim();
              console.log('事件类型:', eventType);
            } else if (line.startsWith('data:')) {
              // 支持有无空格的格式
              const data = line.startsWith('data: ') ? line.slice(6) : line.slice(5);
              eventData += (eventData ? '\n' : '') + data;
              console.log('✅ 发现数据行:', data.substring(0, 50) + (data.length > 50 ? '...' : ''));
            } else if (line.startsWith('id:')) {
              console.log('消息 ID:', line.slice(3).trim());
            } else if (line.startsWith('retry:')) {
              console.log('重试间隔:', line.slice(6).trim());
            } else if (line.startsWith(':')) {
              // 注释行，通常用于保持连接活跃（心跳）
              console.log('💓 心跳/注释:', line);
            } else if (line.trim()) {
              // 非标准格式：可能整行就是数据
              console.log('⚠️ 非标准行格式，尝试作为数据处理:', line);
              eventData += (eventData ? '\n' : '') + line;
            }
          }
          
          // 处理提取到的数据
          if (eventData) {
            processMessage(eventData);
          }
        }
        
        // 如果缓冲区中有数据但没有完整消息，检查是否是非标准格式
        // 有些服务器可能每行发送一条消息，不使用双换行符
        if (buffer.trim() && buffer.includes('\n')) {
          const lines = buffer.split(/\r?\n/);
          // 保留最后一行（可能不完整）
          const lastLine = lines.pop() || '';
          
          for (const line of lines) {
            if (line.startsWith('data:')) {
              const data = line.startsWith('data: ') ? line.slice(6) : line.slice(5);
              if (data.trim()) {
                console.log('处理单行数据（非标准格式）:', data);
                processMessage(data);
              }
            } else if (line.startsWith(':')) {
              console.log('💓 心跳:', line);
            } else if (line.trim() && !line.startsWith('event:') && !line.startsWith('id:') && !line.startsWith('retry:')) {
              // 尝试直接处理非标准格式的数据行
              console.log('尝试处理非标准行:', line);
              processMessage(line);
            }
          }
          
          buffer = lastLine;
        }
        
        console.log('当前缓冲区剩余:', buffer.length > 0 ? `${buffer.substring(0, 50)}...` : '(空)');
        console.log(''); // 空行
      }

      // 连接正常关闭
      if (!this.isClosed && this.shouldReconnect()) {
        this.reconnect();
      }
    } catch (error: any) {
      this.readyState = 2; // CLOSED
      
      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #ff4d4f');
      console.log('%c❌ [SSE] 连接错误', 'color: #ff4d4f; font-weight: bold; font-size: 13px');
      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #ff4d4f');
      console.log('%c📊 [错误详情]:', 'color: #ff4d4f; font-weight: bold');
      console.error(error);
      console.log('%c📊 [连接状态]:', 'color: #ff4d4f; font-weight: bold');
      console.log('ReadyState:', this.readyState);
      console.log('- 0: CONNECTING（正在连接）');
      console.log('- 1: OPEN（已连接）');
      console.log('- 2: CLOSED（已关闭）');
      console.log('%c📊 [重连信息]:', 'color: #ff4d4f; font-weight: bold');
      console.log(`当前重连次数: ${this.reconnectAttempts}`);
      console.log(`最大重连次数: ${this.options.maxReconnectAttempts}`);
      console.log(`是否将重连: ${!this.isClosed && this.shouldReconnect()}`);
      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #ff4d4f');
      console.log(''); // 空行
      
      // 传递错误事件
      const errorEvent = new Event('error');
      this.options.onError?.(errorEvent);

      // 自动重连（除非是 abort）
      if (error.name !== 'AbortError' && !this.isClosed && this.shouldReconnect()) {
        this.reconnect();
      }
    }
  }

  /**
   * 获取 API Key
   */
  private getApiKey(): string {
    try {
      const authData = localStorage.getItem('auth');
      if (authData) {
        const parsed = JSON.parse(authData);
        return parsed.apiKey || '';
      }
    } catch (error) {
      console.warn('获取 API Key 失败:', error);
    }
    return '';
  }

  /**
   * 判断是否应该重连
   */
  private shouldReconnect(): boolean {
    const { maxReconnectAttempts = 5 } = this.options;
    return this.reconnectAttempts < maxReconnectAttempts;
  }

  /**
   * 重连
   */
  private reconnect() {
    this.reconnectAttempts += 1;
    const { reconnectInterval = 3000 } = this.options;

    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #faad14');
    console.log('%c🔄 [SSE] 准备重连', 'color: #faad14; font-weight: bold; font-size: 13px');
    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #faad14');
    console.log(`重连次数: ${this.reconnectAttempts}/${this.options.maxReconnectAttempts}`);
    console.log(`等待时间: ${reconnectInterval}ms`);
    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #faad14');
    console.log(''); // 空行

    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, reconnectInterval);
  }

  /**
   * 关闭连接
   */
  close() {
    this.isClosed = true;

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.abortController) {
      const readyState = this.readyState;
      this.abortController.abort();
      this.abortController = null;
      this.readyState = 2; // CLOSED
      
      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #d9d9d9');
      console.log('%c🔌 [SSE] 连接已关闭', 'color: #d9d9d9; font-weight: bold; font-size: 13px');
      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #d9d9d9');
      console.log(`关闭前状态: ${readyState}`);
      console.log(`重连次数已重置: ${this.reconnectAttempts} -> 0`);
      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #d9d9d9');
      console.log(''); // 空行
    }

    this.reconnectAttempts = 0;
  }

  /**
   * 获取连接状态
   */
  getReadyState(): number {
    return this.readyState;
  }

  /**
   * 判断是否已连接
   */
  isConnected(): boolean {
    return this.readyState === 1; // OPEN
  }
}

export default SSEClient;
